import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiZap } from "react-icons/fi";
import { typography, spacing, components } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { stats } from "../data/aboutData";
import { services } from "../data/servicesData";

const About = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-24 text-secondary scroll-mt-24 md:py-36"
    >
      <style>
        {`
          @keyframes aboutOrbMove {
            0%, 100% {
              transform: translate3d(0, 0, 0) scale(1);
            }
            50% {
              transform: translate3d(28px, -22px, 0) scale(1.08);
            }
          }

          @keyframes aboutFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes aboutLineMove {
            from {
              background-position: 0 0;
            }
            to {
              background-position: 72px 72px;
            }
          }

          .about-orb {
            animation: aboutOrbMove 8s ease-in-out infinite;
          }

          .about-floating-card {
            animation: aboutFloat 5s ease-in-out infinite;
          }

          .about-grid-bg {
            animation: aboutLineMove 16s linear infinite;
          }

          .about-stat-card:hover .about-stat-icon {
            transform: translateY(-4px) rotate(-6deg) scale(1.08);
          }

          @media (prefers-reduced-motion: reduce) {
            .about-orb,
            .about-floating-card,
            .about-grid-bg {
              animation: none;
            }
          }
        `}
      </style>

      {/* Background */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(230,126,34,0.10),transparent_30%),radial-gradient(circle_at_86%_70%,rgba(139,174,102,0.13),transparent_34%),linear-gradient(to_bottom,#ffffff,#fffaf2_52%,#ffffff)]" />

      <div className="about-grid-bg pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(72,103,38,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(72,103,38,0.035)_1px,transparent_1px)] bg-[size:72px_72px] opacity-70" />

      <div className="about-orb pointer-events-none absolute -left-24 bottom-20 h-80 w-80 rounded-full bg-accent/[0.14] blur-3xl" />
      <div className="about-orb pointer-events-none absolute -right-24 top-16 h-80 w-80 rounded-full bg-primary/[0.12] blur-3xl [animation-delay:1.4s]" />

      <div className={`relative z-10 ${spacing.container}`}>
        <div
          className="relative overflow-hidden rounded-[2.5rem] border border-secondary/10 bg-white/85 p-7 shadow-2xl shadow-secondary/10 backdrop-blur-md md:p-12"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 30}px) scale(${
              0.96 + visibility * 0.04
            })`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <div className="grid gap-12 lg:grid-cols-[1.15fr,0.85fr] lg:items-center">
            {/* Left content */}
            <div
              className="relative z-10"
              style={{
                opacity: visibility,
                transform: `translateX(${(1 - visibility) * -40}px)`,
                transition: "transform 0.05s linear, opacity 0.1s linear",
              }}
            >
              <p className={`${typography.label} text-gray-400`}>About O3S</p>

              <h2 className={`${typography.heading.h2} mt-5 text-secondary`}>
                Boutique digital studio for small businesses
              </h2>

              <div
                className="mt-6 h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent"
                style={{
                  transform: `scaleX(${visibility})`,
                  transformOrigin: "left",
                }}
              />

              <p className={`${typography.body.base} mt-7 max-w-2xl`}>
                O3S combines consulting, design, development, and marketing in
                one place, so your online presence feels like a single, coherent
                product.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {services.map((service, index) => (
                  <div
                    key={service.title}
                    className="rounded-2xl border border-secondary/10 bg-white/80 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-lg"
                    style={{
                      opacity: visibility,
                      transform: `translateY(${
                        (1 - visibility) * (14 + index * 6)
                      }px)`,
                      transition:
                        "transform 0.05s linear, opacity 0.12s linear",
                      transitionDelay: `${index * 40}ms`,
                    }}
                  >
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <FiZap />
                    </div>
                    <p className={typography.card.base}>{service.title}</p>
                  </div>
                ))}
              </div>

              <div
                className="mt-9"
                style={{
                  opacity: Math.max(0.4, visibility),
                  transform: `translateY(${(1 - visibility) * 12}px)`,
                  transition: "transform 0.05s linear, opacity 0.1s linear",
                }}
              >
                <Link
                  to="/about"
                  className={`${components.button.primary} text-sm`}
                >
                  Discover more
                  <FiArrowRight />
                </Link>
              </div>
            </div>

            {/* Right visual / stats */}
            <div
              className="relative z-10"
              style={{
                opacity: visibility,
                transform: `translateX(${(1 - visibility) * 45}px)`,
                transition: "transform 0.05s linear, opacity 0.12s linear",
              }}
            >
              <div className="grid gap-5">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;

                  return (
                    <div
                      key={stat.label}
                      className="about-stat-card group relative overflow-hidden rounded-[2rem] border border-secondary/10 bg-white p-6 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:border-primary/30 hover:shadow-2xl"
                      style={{
                        opacity: visibility,
                        transform: `translateY(${
                          (1 - visibility) * (28 + index * 10)
                        }px)`,
                        transition:
                          "transform 0.05s linear, opacity 0.12s linear",
                        transitionDelay: `${index * 50}ms`,
                      }}
                    >
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.07] via-transparent to-accent/[0.10] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                      <div className="relative z-10 flex items-start gap-5">
                        <div className="about-stat-icon flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl text-primary transition-all duration-500 group-hover:bg-primary group-hover:text-white">
                          <Icon />
                        </div>

                        <div>
                          <p className={`${typography.label} text-gray-400`}>
                            {stat.label}
                          </p>

                          <p
                            className={`${typography.card.large} text-3xl mt-2`}
                          >
                            {stat.value}
                          </p>

                          <p className={`${typography.body.base} mt-3`}>
                            {stat.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
